import { evaluateRelation } from "../relation/Relation";
import type { ExpressionValue } from "../expression/expressionFunctions";
import type { FlowBranchItem } from "./FlowBranchItem";
import type { FlowItem } from "./FlowItem";
import type { Uid } from "@/helpers/utility/Id";

type SimulationFlowBranch = {
  uid: `flowBranch_${number}`;
  id: number;
  elementUid: Uid<'resource'|'target'>;
  value: number;
  originalValue: number;
  flow: FlowItem;
  flowBranches: FlowBranchItem[];
  validated: boolean;
  limitsFlow: boolean;
  limit?: { branch: SimulationFlowBranch, value: number };
};

export function calculateFlowBranches(
  flowItems: { [flowId: number]: FlowItem },
  branchesByFlow: { [flowId: number]: FlowBranchItem[] },
  elementValues: { [uid: string]: ExpressionValue }
) {
  const elementList: {
    [uid: string]: {
      uid: string;
      value: number;
      minValue: number | null;
      maxValue: number | null;
      valueSum?: number;
      flows: SimulationFlowBranch[];
    };
  } = {};

  const branchItems: Map<number, SimulationFlowBranch> = new Map();

  // get impulses for all flows in this round
  for (const flowId in branchesByFlow) {
    const flow = flowItems[flowId];
    if (!flow) {
      console.log("flow " + flowId + " not passed");
      continue;
    }
    const flowValue = elementValues[flow.uid];

    const branches = branchesByFlow[flowId];

    for (const branch of branches) {
      const branchValue = evaluateRelation(branch, Number(flowValue.value));

      const elementUid: Uid<'target'|'resource'> = `${branch.elementType}_${branch.elementId}`;
      const elementValue = elementValues[elementUid];
      if (elementValue == null) {
        console.error("element value not found", elementUid);
        continue;
      }
      if (!elementList[elementUid]) {
        elementList[elementUid] = {
          uid: elementUid,
          value: Number(elementValue.value),
          minValue: elementValue.minValue,
          maxValue: elementValue.maxValue,
          valueSum: undefined,
          flows: []
        };
      }

      const branchItem: SimulationFlowBranch = {
        uid: branch.uid,
        id: branch.id,
        elementUid: elementUid,
        value: branchValue,
        originalValue: branchValue,
        flow: flow,
        flowBranches: branches,
        validated: false,
        limitsFlow: branch.limitsFlow,
        limit: undefined
      };
      branchItems.set(branch.id, branchItem);
      elementList[elementUid].flows.push(branchItem);
    }
  }

  const elementListArray = Object.values(elementList).sort(
    (a, b) => a.flows.length - b.flows.length
  );

  const dirtyElements = new Set<string>(elementListArray.map((e) => e.uid));

  // go downwards through the flow branches
  // (validate downflow limits)
  for (const elementUid of dirtyElements) {
    const element = elementList[elementUid];
    const elementValue = element.value;
    const negativeBranches = element.flows.filter((branch) => branch.originalValue < 0);
    const positiveBranches = element.flows.filter((branch) => branch.originalValue > 0);

    const negativeSum = negativeBranches.reduce(
      (sum, branch) => sum + (branch.limit ? branch.limit.value : branch.originalValue),
      0
    );
    const positiveSum = positiveBranches.reduce(
      (sum, branch) => sum + (branch.limit ? branch.limit.value : branch.originalValue),
      0
    );

    // calculate a scale factor to find out by how much the branches
    // need to be scaled down if were trying to take more than available
    const negativeNeeded = Math.abs(negativeSum);
    const negativeAvailable = element.minValue == null ? Infinity : Math.abs(
      elementValue - element.minValue + positiveSum
    );
    const negativeScale = Math.min(negativeAvailable / negativeNeeded, 1);

    const positiveNeeded = Math.abs(positiveSum);
    const positiveAvailable = element.maxValue == null ? Infinity : Math.abs(
      elementValue - element.maxValue + negativeSum
    );
    const positiveScale = Math.min(positiveAvailable / positiveNeeded, 1);

    dirtyElements.delete(element.uid);

    // console.log(
    //   "handle",
    //   element.uid,
    //   element.value,
    //   -negativeNeeded,
    //   positiveNeeded
    // );

    for (const branch of negativeBranches) {
      if (negativeNeeded == 0) continue;
      const newValue = (branch.limit ? branch.limit.value : branch.originalValue) * negativeScale;
      if (newValue == branch.value) {
        // console.log("branch valid", branch.value);
        branch.validated = true;
      } else {
        const valueBefore = branch.value;
        // console.log("limit branch", branch.id, valueBefore, "=>", newValue);
        branch.value = newValue;
        branch.validated = true;

        if (branch.limitsFlow) {
          limitFlow(branch, newValue / valueBefore, dirtyElements, branchItems);
        }
      }
    }

    for (const branch of positiveBranches) {
      if (positiveNeeded == 0) continue;
      const newValue = (branch.limit ? branch.limit.value : branch.originalValue) * positiveScale;
      if (newValue == branch.value) {
        // console.log("branch valid", branch.value);
        branch.validated = true;
      } else {
        const valueBefore = branch.value;
        // console.log("limit branch", valueBefore, "=>", branchValue);
        branch.value = newValue;
        branch.validated = true;

        if (branch.limitsFlow) {
          limitFlow(branch, newValue / valueBefore, dirtyElements, branchItems);
        }
      }
    }
  }

  const branchValues: { [uid: string]: number } = {};
  branchItems.forEach((branch) => (branchValues[branch.uid] = branch.value));
  // console.log("flow branch values", branchValues);

  return branchItems;
}

function limitFlow(
  branch: SimulationFlowBranch,
  scale: number,
  dirtyElements: Set<string>,
  branchItems: Map<number, SimulationFlowBranch>
) {
  for (const otherBranch of branch.flowBranches) {
    // don't limit the branch itself
    if (otherBranch.id == branch.id) continue;

    const otherBranchItem = branchItems.get(otherBranch.id);
    if (!otherBranchItem) {
      console.warn("branch", otherBranch.id, "not found");
      continue;
    }

    otherBranchItem.validated = false;
    
    const otherBranchValue = otherBranchItem.value;
    otherBranchItem.value = otherBranchValue * scale;
    otherBranchItem.limit = { branch, value: otherBranchItem.value };
    // console.log(
    //   "limit connected branch",
    //   otherBranchValue,
    //   "=>",
    //   otherBranchItem.value
    // );

    // re-add the element to the dirty list
    const elementUid = `${otherBranch.elementType}_${otherBranch.elementId}`;
    dirtyElements.add(elementUid);
    // console.log("re-add element", elementUid);
  }
}